export const STATUS_FILTERS = ['all', 'open', 'resolved', 'ignored'] as const;
export const GROUP_BY_OPTIONS = [
  'decision',
  'pr',
  'rule',
  'file',
  'area',
  'none',
] as const;
export const SORT_OPTIONS = [
  'default',
  'file',
  'severity',
  'category',
] as const;
export const VIEW_OPTIONS = ['all', 'ci', 'repo-scan'] as const;
export const PR_VIEW_OPTIONS = [
  'needs-attention',
  'blocking',
  'advisory',
  'passing',
  'all',
  'introduced',
  'safe-fixes',
  'policy-needed',
  'manual-review',
  'existing-debt',
] as const;
export const SEVERITY_OPTIONS = [
  'critical',
  'serious',
  'moderate',
  'minor',
  'error',
  'warning',
  'info',
] as const;
export const DECISION_ACTIONABILITY_OPTIONS = [
  'safe_fix',
  'guided_fix',
  'policy_needed',
  'manual_review',
  'blocked',
] as const;
export const DECISION_POLICY_STATUS_OPTIONS = [
  'declared',
  'inferred',
  'generic_default',
  'missing',
  'not_required',
] as const;
export const DECISION_KIND_OPTIONS = [
  'token_decision',
  'token_raw_value',
  'token_duplicate_value',
  'token_off_scale_value',
  'component_adoption',
  'component_api_repair',
  'component_consolidation',
  'component_policy_violation',
  'a11y_regression',
  'policy_readiness',
  'tailwind_token_decision',
  'tailwind_raw_color_token',
  'tailwind_unknown_palette_token',
  'tailwind_arbitrary_color',
  'tailwind_arbitrary_spacing',
  'tailwind_off_scale_spacing',
  'css_raw_value',
  'css_off_scale_spacing',
  'other',
] as const;

export type FindingsStatusFilter = (typeof STATUS_FILTERS)[number];
export type FindingsGroupBy = (typeof GROUP_BY_OPTIONS)[number];
export type FindingsSort = (typeof SORT_OPTIONS)[number];
export type FindingsView = (typeof VIEW_OPTIONS)[number];
export type FindingsPrView = (typeof PR_VIEW_OPTIONS)[number];
export type FindingsSeverity = (typeof SEVERITY_OPTIONS)[number];
export type FindingsDecisionActionability =
  (typeof DECISION_ACTIONABILITY_OPTIONS)[number];
export type FindingsDecisionPolicyStatus =
  (typeof DECISION_POLICY_STATUS_OPTIONS)[number];
export type FindingsDecisionKind = (typeof DECISION_KIND_OPTIONS)[number];

export const CATEGORY_LABELS: Record<string, string> = {
  spacing: 'Spacing',
  typography: 'Typography',
  color: 'Color',
  sizing: 'Sizing',
  radius: 'Radius',
  shadow: 'Shadow',
  border: 'Border',
  animation: 'Animation',
  'z-index': 'Z-Index',
  other: 'Other',
};

export interface FindingsSearch {
  pr?: number;
  page?: number;
  prView?: FindingsPrView;
  q?: string;
  status?: FindingsStatusFilter;
  severity?: FindingsSeverity;
  code?: string;
  rule?: string;
  category?: string;
  groupBy?: FindingsGroupBy;
  sort?: FindingsSort;
  view?: FindingsView;
  actionability?: FindingsDecisionActionability;
  policyStatus?: FindingsDecisionPolicyStatus;
  decisionKind?: FindingsDecisionKind;
  decisionKey?: string;
  project?: string;
  /**
   * Filter to findings whose `suggestedToken` matches this token, normalised
   * across CSS / SCSS / DTCG sources. Used by deep links from the Tokens
   * page so a click on a token's "linked findings" surface lands on a
   * pre-filtered findings view.
   */
  token?: string;
  /**
   * Filter to findings attributed to a single component, by `componentKey`.
   * Resolved client-side to the component's `lastSeenFilePath` via the
   * registry. Used by deep links from the component detail page's Drift
   * signal panel.
   */
  component?: string;
  /**
   * Filter to findings in a single product area, by `areaId` (topology layer).
   * `'unassigned'` selects findings with no resolved area. Used by the Area
   * filter and deep links from the area-detail / overview surfaces.
   */
  area?: string;
}

// Token-key normalisation lives in `@/lib/findings-token-key` so the
// Convex `getByProject` query can use the same matching logic when it
// pushes the token filter server-side. We re-export here for callers
// that already imported from this module.
export {
  normalizeFindingTokenKey,
  tokenKeyVariants,
  tokenKeysMatch,
} from '@/lib/findings-token-key';

function isStatusFilter(value: unknown): value is FindingsStatusFilter {
  return (
    typeof value === 'string' &&
    STATUS_FILTERS.includes(value as FindingsStatusFilter)
  );
}

function isGroupBy(value: unknown): value is FindingsGroupBy {
  return (
    typeof value === 'string' &&
    GROUP_BY_OPTIONS.includes(value as FindingsGroupBy)
  );
}

function isSort(value: unknown): value is FindingsSort {
  return (
    typeof value === 'string' && SORT_OPTIONS.includes(value as FindingsSort)
  );
}

function isView(value: unknown): value is FindingsView {
  return (
    typeof value === 'string' && VIEW_OPTIONS.includes(value as FindingsView)
  );
}

function isPrView(value: unknown): value is FindingsPrView {
  return (
    typeof value === 'string' &&
    PR_VIEW_OPTIONS.includes(value as FindingsPrView)
  );
}

function isSeverity(value: unknown): value is FindingsSeverity {
  return (
    typeof value === 'string' &&
    SEVERITY_OPTIONS.includes(value as FindingsSeverity)
  );
}

function isDecisionActionability(
  value: unknown,
): value is FindingsDecisionActionability {
  return (
    typeof value === 'string' &&
    DECISION_ACTIONABILITY_OPTIONS.includes(
      value as FindingsDecisionActionability,
    )
  );
}

function isDecisionPolicyStatus(
  value: unknown,
): value is FindingsDecisionPolicyStatus {
  return (
    typeof value === 'string' &&
    DECISION_POLICY_STATUS_OPTIONS.includes(
      value as FindingsDecisionPolicyStatus,
    )
  );
}

function isDecisionKind(value: unknown): value is FindingsDecisionKind {
  return (
    typeof value === 'string' &&
    DECISION_KIND_OPTIONS.includes(value as FindingsDecisionKind)
  );
}

export function validateFindingsSearch(
  search: Record<string, unknown>,
): FindingsSearch {
  const next: FindingsSearch = {};

  if (typeof search.pr === 'number' && Number.isFinite(search.pr)) {
    next.pr = search.pr;
  } else if (typeof search.pr === 'string') {
    const pr = Number.parseInt(search.pr, 10);
    if (Number.isFinite(pr) && pr > 0) next.pr = pr;
  }

  if (typeof search.page === 'number' && Number.isFinite(search.page)) {
    const page = Math.max(1, Math.floor(search.page));
    if (page > 1) next.page = page;
  } else if (typeof search.page === 'string') {
    const page = Number.parseInt(search.page, 10);
    if (Number.isFinite(page) && page > 1) next.page = page;
  }

  if (isPrView(search.prView) && search.prView !== 'needs-attention') {
    next.prView = search.prView;
  }

  if (typeof search.q === 'string' && search.q.trim()) {
    next.q = search.q;
  }

  if (isStatusFilter(search.status) && search.status !== 'all') {
    next.status = search.status;
  }

  if (isSeverity(search.severity)) {
    next.severity = search.severity;
  }

  if (typeof search.code === 'string' && search.code.trim()) {
    next.code = search.code;
  }

  if (typeof search.rule === 'string' && search.rule.trim()) {
    next.rule = search.rule;
  }

  if (typeof search.category === 'string' && search.category.trim()) {
    next.category = search.category;
  }

  if (isGroupBy(search.groupBy)) {
    next.groupBy = search.groupBy;
  }

  if (isSort(search.sort) && search.sort !== 'default') {
    next.sort = search.sort;
  }

  if (isView(search.view) && search.view !== 'all') {
    next.view = search.view;
  }

  if (isDecisionActionability(search.actionability)) {
    next.actionability = search.actionability;
  }

  if (isDecisionPolicyStatus(search.policyStatus)) {
    next.policyStatus = search.policyStatus;
  }

  if (isDecisionKind(search.decisionKind)) {
    next.decisionKind = search.decisionKind;
  }

  if (typeof search.decisionKey === 'string' && search.decisionKey.trim()) {
    next.decisionKey = search.decisionKey;
  }

  if (typeof search.project === 'string' && search.project) {
    next.project = search.project;
  }

  if (typeof search.token === 'string' && search.token.trim()) {
    next.token = search.token;
  }

  if (typeof search.component === 'string' && search.component.trim()) {
    next.component = search.component;
  }

  if (typeof search.area === 'string' && search.area.trim()) {
    next.area = search.area;
  }

  return next;
}
